import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase/config';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { useSubscription } from '../contexts/SubscriptionContext';

interface StatsData {
  invoices: {
    count: number;
    totalAmount: number;
    recentDate: Date | null;
  };
  views: {
    total: number;
    lastWeek: number;
    lastMonth: number;
    locations: Record<string, number>;
  };
  shares: {
    total: number;
    lastWeek: number;
    bySocial: Record<string, number>;
    channels: {
      native: number;
      email: number;
      sms: number;
      facebook: number;
      twitter: number;
      kakao: number;
      whatsapp: number;
    };
  };
  qrScans: {
    total: number;
    lastWeek: number;
    lastMonth: number;
  };
  vcardDownloads: {
    total: number;
    lastWeek: number;
    lastMonth: number;
  };
  urlCopies: {
    total: number;
    lastWeek: number;
    lastMonth: number;
  };
  conversionRates: {
    contactClickRate: number;
    linkClickRate: number;
    downloadRate: number;
    shareRate: number;
  };
  clickDetails?: {
    totalContactClicks: number;
    phoneClicks: number;
    emailClicks: number;
    websiteClicks: number;
    socialClicks: number;
    linkClicks: number;
  };
}

const StatsDetailPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { userPlan, isPro, isStandard, isTrialActive, isExpired, isLoading: subscriptionLoading } = useSubscription();
  const navigate = useNavigate();
  const { cardId, ownerId } = useParams<{ cardId: string; ownerId?: string }>();

  // 🔐 프리미엄 기능 접근 권한 확인 (만료된 고객과 무료 트라이얼 제외)
  const canAccessStats = (isPro || isStandard) && !isExpired;

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasData, setHasData] = useState(false);
  const [cardData, setCardData] = useState<any>(null);
  const [stats, setStats] = useState<StatsData>({
    invoices: {
      count: 0,
      totalAmount: 0,
      recentDate: null,
    },
    views: {
      total: 0,
      lastWeek: 0,
      lastMonth: 0,
      locations: {}
    },
    shares: {
      total: 0,
      lastWeek: 0,
      bySocial: {},
      channels: {
        native: 0,
        email: 0,
        sms: 0,
        facebook: 0,
        twitter: 0,
        kakao: 0,
        whatsapp: 0
      }
    },
    qrScans: {
      total: 0,
      lastWeek: 0,
      lastMonth: 0
    },
    vcardDownloads: {
      total: 0,
      lastWeek: 0,
      lastMonth: 0
    },
    urlCopies: {
      total: 0,
      lastWeek: 0,
      lastMonth: 0
    },
    conversionRates: {
      contactClickRate: 0,
      linkClickRate: 0,
      downloadRate: 0,
      shareRate: 0
    }
  });

  const auth = getAuth();
  const userId = auth.currentUser?.uid;

  // 🔐 프리미엄 접근 제한 확인
  useEffect(() => {
    // 구독 정보 로딩 중이면 대기
    if (subscriptionLoading) {
      console.log('⏳ [StatsDetailPage] 구독 정보 로딩 중...');
      return;
    }

    console.log('🔒 [StatsDetailPage] 구독 상태 체크:', {
      userPlan,
      isPro,
      isStandard,
      isTrialActive,
      isExpired,
      canAccessStats,
      subscriptionLoading
    });

    // 🚨 강제 조건 체크 - 하나라도 만료 조건이면 차단
    if (isExpired || (!isPro && !isStandard)) {
      console.log('🚨 [StatsDetailPage] 강제 접근 차단 - 조건:', {
        isExpiredBlock: isExpired,
        notPremiumBlock: !isPro && !isStandard,
        finalDecision: 'BLOCKED'
      });

      if (isExpired) {
        console.log('❌ [StatsDetailPage] 구독 만료됨 - 통계 접근 차단');
        setError('subscription_expired');
      } else {
        console.log('❌ [StatsDetailPage] 프리미엄 플랜 필요 - 통계 접근 차단');
        setError('premium_required');
      }
      setLoading(false);
      return;
    }

    // 프리미엄 사용자만 통계 데이터 로드
    fetchStats();
  }, [canAccessStats, isExpired, userPlan, isPro, isStandard, subscriptionLoading]);

  const fetchStats = useCallback(async () => {
    console.log('📊 [StatsDetailPage] fetchStats called with:', { userId, cardId, ownerId });
    
    if (!userId) {
      console.warn('❌ [StatsDetailPage] userId가 없습니다.');
      setLoading(false);
      return;
    }
    
    if (!cardId) {
      console.warn('❌ [StatsDetailPage] cardId가 없습니다.', { cardId });
      setLoading(false);
      return;
    }

    // 🔥 중요: 카드 소유자 ID 사용 (ownerId 우선, 없으면 userId)
    const cardOwnerId = ownerId || userId;
    if (!cardOwnerId) {
      console.warn('❌ [StatsDetailPage] cardOwnerId가 없습니다.');
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      console.log('🔍 [StatsDetailPage] Firebase에서 데이터 가져오기 시작...');
      
      const cardRef = doc(collection(db, 'users', cardOwnerId, 'cards'), cardId);
      console.log('📄 [StatsDetailPage] 카드 참조 경로:', `users/${cardOwnerId}/cards/${cardId}`);
      const cardSnap = await getDoc(cardRef);
      
      if (cardSnap.exists()) {
        const cardData = cardSnap.data();
        console.log('✅ [StatsDetailPage] 카드 데이터 로드 성공:', cardData);
        
        // 앱과 동일한 안전한 데이터 추출 (null과 undefined 모두 체크)
        const viewCount = (cardData.viewCount !== undefined && cardData.viewCount !== null) ? cardData.viewCount : 0;
        const shareCount = (cardData.shareCount !== undefined && cardData.shareCount !== null) ? cardData.shareCount : 0;
        const qrScanCount = (cardData.qrScanCount !== undefined && cardData.qrScanCount !== null) ? cardData.qrScanCount : 0;
        const vCardDownloadCount = (cardData.vCardDownloadCount !== undefined && cardData.vCardDownloadCount !== null) ? cardData.vCardDownloadCount : 0;
        const urlCopyCount = (cardData.urlCopyCount !== undefined && cardData.urlCopyCount !== null) ? cardData.urlCopyCount : 0;
        const contactClicks = (cardData.contactClicks !== undefined && cardData.contactClicks !== null) ? cardData.contactClicks : 0;
        const linkClicks = (cardData.linkClicks !== undefined && cardData.linkClicks !== null) ? cardData.linkClicks : 0;
        const phoneClicks = (cardData.phoneClicks !== undefined && cardData.phoneClicks !== null) ? cardData.phoneClicks : 0;
        const emailClicks = (cardData.emailClicks !== undefined && cardData.emailClicks !== null) ? cardData.emailClicks : 0;
        const websiteClicks = (cardData.websiteClicks !== undefined && cardData.websiteClicks !== null) ? cardData.websiteClicks : 0;
        const socialClicks = (cardData.socialClicks !== undefined && cardData.socialClicks !== null) ? cardData.socialClicks : 0;
        
        console.log('🔧 [StatsDetailPage] 누락된 필드 초기화 완료:', {
          viewCount,
          shareCount,
          qrScanCount,
          vCardDownloadCount,
          urlCopyCount,
          contactClicks,
          linkClicks,
          phoneClicks,
          emailClicks,
          websiteClicks,
          socialClicks
        });

        // ===== [START] 인보이스 데이터 계산 로직 =====
        console.log('📄 [StatsDetailPage] 인보이스 데이터 가져오기 시작...');
        // 앱과 동일한 경로 사용: users/{cardOwnerId}/invoices에서 cardId로 필터링
        const invoicesRef = collection(db, 'users', cardOwnerId, 'invoices');
        const invoicesSnap = await getDocs(invoicesRef);

        let finalCount = 0;
        let totalAmount = 0;
        let recentDate: Date | null = null;

        invoicesSnap.forEach(doc => {
          const invoiceData = doc.data();
          if (invoiceData.cardId === cardId) {
            finalCount++;
            totalAmount += invoiceData.amount || 0;
            if (invoiceData.createdAt && (!recentDate || invoiceData.createdAt.toDate() > recentDate)) {
              recentDate = invoiceData.createdAt.toDate();
            }
          }
        });
        
        console.log('📄 [StatsDetailPage] 인보이스 집계 결과:', { finalCount, totalAmount, recentDate });
        // ===== [END] 인보이스 데이터 계산 로직 =====

        // 전체 연락처 클릭 (전화, 이메일, 웹사이트, 소셜 등)
        const totalContactClicks = contactClicks + phoneClicks + emailClicks + websiteClicks + socialClicks;

        // 전환율 계산 (조회수가 0이면 0으로 처리)
        const contactClickRate = viewCount > 0 ? ((totalContactClicks / viewCount) * 100) : 0;
        const linkClickRate = viewCount > 0 ? ((linkClicks / viewCount) * 100) : 0;

        // 🎯 통계 데이터 구조
        const statsData: StatsData = {
          invoices: {
            count: finalCount,
            totalAmount: totalAmount,
            recentDate: recentDate,
          },
          views: {
            total: viewCount,
            lastWeek: 0,
            lastMonth: 0,
            locations: {}
          },
          shares: {
            total: shareCount,
            lastWeek: 0,
            bySocial: {},
            channels: cardData.shareChannels || {
              native: 0,
              email: 0,
              sms: 0,
              facebook: 0,
              twitter: 0,
              kakao: 0,
              whatsapp: 0
            }
          },
          qrScans: {
            total: qrScanCount,
            lastWeek: 0,
            lastMonth: 0
          },
          vcardDownloads: {
            total: vCardDownloadCount,
            lastWeek: 0,
            lastMonth: 0
          },
          urlCopies: {
            total: urlCopyCount,
            lastWeek: 0,
            lastMonth: 0
          },
          conversionRates: {
            contactClickRate: contactClickRate,
            linkClickRate: linkClickRate,
            downloadRate: viewCount > 0 ? (vCardDownloadCount / viewCount * 100) : 0,
            shareRate: viewCount > 0 ? (shareCount / viewCount * 100) : 0
          },
          // 🔥 추가 클릭 상세 정보
          clickDetails: {
            totalContactClicks,
            phoneClicks,
            emailClicks,
            websiteClicks,
            socialClicks,
            linkClicks
          }
        };

        // URL 복사 및 클릭 데이터 디버깅 추가

        // 📊 URL 복사 통계 상세 분석
        console.log('◆◇◆◇ [StatsDetailPage] URL 복사 통계 상세 분석:', {
          '◆◇◆◇ 원본 데이터': {
            urlCopyCount: cardData.urlCopyCount,
            type: typeof cardData.urlCopyCount,
            isNull: cardData.urlCopyCount === null,
            isUndefined: cardData.urlCopyCount === undefined
          },
          '✉️ 처리된 데이터': {
            urlCopiesTotal: statsData.urlCopies.total,
            type: typeof statsData.urlCopies.total
          },
          '🎯 표시 조건': {
            condition: 'stats?.urlCopies?.total >= 0',
            result: statsData.urlCopies.total >= 0
          }
        });

        console.log('📊 [StatsDetailPage] 최종 통계 데이터:', {
          cardOwnerId,
          viewCount: cardData.viewCount,
          shareCount: cardData.shareCount,
          qrScanCount: cardData.qrScanCount,
          vCardDownloadCount: cardData.vCardDownloadCount,
          urlCopyCount: cardData.urlCopyCount,
          contactClicks: cardData.contactClicks,
          linkClicks: cardData.linkClicks,
          phoneClicks: cardData.phoneClicks,
          emailClicks: cardData.emailClicks,
          websiteClicks: cardData.websiteClicks,
          socialClicks: cardData.socialClicks,
          invoiceCount: finalCount,
          statsData: statsData
        });

        setStats(statsData);
        setCardData(cardData);
        setHasData(true);

        console.log('✅ [StatsDetailPage] 모든 데이터 설정 완료');

      } else {
        console.error('❌ [StatsDetailPage] 카드 문서를 찾을 수 없습니다:', { cardId, cardOwnerId });
        setHasData(false);
      }

    } catch (error) {
      console.error('❌ [StatsDetailPage] 통계 데이터 가져오기 실패:', error);
      setError(`통계 데이터 로드 실패: ${error instanceof Error ? error.message : String(error)}`);
      setHasData(false);
    } finally {
      setLoading(false);
      console.log('🏁 [StatsDetailPage] fetchStats 완료');
    }
  }, [userId, cardId, ownerId, t]);

  // 금액을 다국어 형식으로 포맷팅하는 함수
  const formatCurrency = (amount: number) => {
    if (typeof amount !== 'number') {
      const currencySymbol = i18n.language === 'ko' ? '' : '$';
      const currencyUnit = i18n.language === 'ko' ? '원' : '';
      return `${currencySymbol}0${currencyUnit}`;
    }

    const currentLanguage = i18n.language;

    if (currentLanguage === 'ko') {
      return `${amount.toLocaleString('ko-KR')}원`;
    } else {
      return `$${amount.toLocaleString('en-US')}`;
    }
  };

  // 날짜를 다국어 형식으로 포맷팅하는 함수
  const formatDate = (date: Date | null) => {
    if (!date || !(date instanceof Date)) return t('common.none') || 'None';
    const currentLanguage = i18n.language;

    try {
      if (currentLanguage === 'ko') {
        return date.toLocaleDateString('ko-KR', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      } else {
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      }
    } catch (e) {
      console.error("날짜 포맷팅 오류:", e);
      return t('common.invalidDate') || 'Invalid Date';
    }
  };

  // 데이터 없음 표시 컴포넌트
  const NoDataMessage: React.FC<{ message: string }> = ({ message }) => (
    <div className="flex flex-col items-center justify-center p-4 text-gray-500">
      <svg className="w-6 h-6 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
      <span className="text-sm text-center">{message || t('stats.noData')}</span>
    </div>
  );

  // 🔐 프리미엄 접근 제한 화면
  if (error === 'premium_required') {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto p-6">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="text-6xl mb-4">💎</div>
            <h1 className="text-2xl font-bold mb-4 text-gray-800">
              {t('stats.premiumRequired.title') || '프리미엄 기능'}
            </h1>
            <p className="text-gray-600 mb-6">
              {t('stats.premiumRequired.description') || t('stats.premiumRequired.descriptionDefault')}
            </p>
            <p className="text-sm text-gray-500 mb-8">
              {t('stats.premiumRequired.currentPlan', { plan: t(`plan.${userPlan}`) }) || `현재 플랜: ${t(`plan.${userPlan}`)}${isTrialActive ? ' (무료 체험 중)' : ''}`}
            </p>

            <button
              className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-3 px-6 rounded-full mb-4 transition-colors"
              onClick={() => navigate('/paywall', { state: { limitType: 'stats_access' } })}
            >
              {t('stats.premiumRequired.upgrade') || '플랜 업그레이드'}
            </button>

            <button
              className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 px-6 rounded-full transition-colors"
              onClick={() => navigate('/')}
            >
              {typeof t('common.backToHome') === 'string'
                ? t('common.backToHome')
                : '홈으로 돌아가기'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 🔐 구독 만료 화면
  if (error === 'subscription_expired') {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto p-6">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="text-6xl mb-4">⏰</div>
            <h1 className="text-2xl font-bold mb-4 text-gray-800">
              {t('subscription.expired.title')}
            </h1>
            <p className="text-gray-600 mb-6">
              {t('subscription.expired.statsBlocked')}
            </p>
            <p className="text-sm text-gray-500 mb-8">
              {t('subscription.expired.planInfo', { plan: t(`plan.${userPlan}`) || userPlan })}
            </p>

            <button
              className="bg-slate-600 hover:bg-slate-700 text-white font-bold py-3 px-6 rounded-full mb-4 transition-colors"
              onClick={() => navigate('/paywall', { state: { limitType: 'subscription_expired' } })}
            >
              {t('subscription.renewNow')}
            </button>

            <button
              className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 px-6 rounded-full transition-colors"
              onClick={() => navigate('/')}
            >
              {t('common.backToHome')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('stats.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded"
            onClick={() => navigate(-1)}
          >
            {t('common.back')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto p-6">
        {/* 헤더 */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 hover:text-gray-800 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {t('common.back')}
          </button>
          <h1 className="text-2xl font-bold text-gray-800">{t('stats.title')}</h1>
          <div className="w-10"></div>
        </div>

        {/* 카드 기본 정보 */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-2">
            {cardData?.name || t('common.noName')}
          </h2>
          <p className="text-gray-600">
            {cardData?.jobTitle || ''} {cardData?.company ? `| ${cardData.company}` : ''}
          </p>
        </div>

        {!hasData && (
          <div className="bg-white rounded-lg shadow-md p-8 text-center mb-6">
            <div className="text-5xl mb-4">⏰</div>
            <h3 className="text-lg font-bold text-gray-800 mb-2">{t('stats.noData')}</h3>
            <p className="text-gray-600">{t('stats.noDataSub')}</p>
          </div>
        )}

        {/* 인보이스 통계 카드 */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center mb-4 pb-2 border-b border-gray-200">
            <div className="text-purple-600 mr-3">📄</div>
            <h3 className="text-lg font-bold text-gray-800">{t('stats.invoiceStats')}</h3>
          </div>

          {stats?.invoices?.count > 0 ? (
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">{t('stats.totalIssued')}</span>
                <span className="font-semibold text-gray-800">{stats.invoices.count}{t('stats.units.times')}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">{t('stats.totalRevenue', '총 매출액')}</span>
                <span className="font-semibold text-gray-800">{formatCurrency(stats.invoices.totalAmount)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">{t('stats.recentIssueDate', '최근 발행일')}</span>
                <span className="font-semibold text-gray-800">{formatDate(stats.invoices.recentDate)}</span>
              </div>
            </div>
          ) : (
            <NoDataMessage message={t('stats.noInvoiceData')} />
          )}
        </div>

        {/* 조회수 통계 카드 */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center mb-4 pb-2 border-b border-gray-200">
            <div className="text-blue-600 mr-3">👁️</div>
            <h3 className="text-lg font-bold text-gray-800">{t('stats.viewStats')}</h3>
          </div>

          {(stats?.views?.total >= 0) ? (
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('stats.totalViews')}</span>
              <span className="font-semibold text-gray-800">{stats.views.total}{t('stats.units.times')}</span>
            </div>
          ) : (
            <NoDataMessage message={t('stats.noViewData')} />
          )}
        </div>

        {/* 공유 통계 카드 */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center mb-4 pb-2 border-b border-gray-200">
            <div className="text-green-600 mr-3">📤</div>
            <h3 className="text-lg font-bold text-gray-800">{t('stats.shareStats')}</h3>
          </div>
          
          {(stats?.shares?.total >= 0) ? (
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">{t('stats.totalShares')}</span>
                <span className="font-semibold text-gray-800">{stats.shares.total}{t('stats.units.times')}</span>
              </div>
              
              {stats.conversionRates?.shareRate > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">{t('stats.shareRate') || '공유율'}</span>
                  <span className="font-semibold text-gray-800">{stats.conversionRates.shareRate.toFixed(1)}%</span>
                </div>
              )}

              {/* 채널별 공유 상세 */}
              {stats.shares.channels && Object.values(stats.shares.channels).some(val => val > 0) && (
                <>
                  <div className="mt-4 pt-3 border-t border-gray-200">
                    <h4 className="font-semibold text-gray-800 mb-3">{t('stats.shareChannels') || '공유 채널'}</h4>
                    <div className="space-y-2">
                      {Object.entries(stats.shares.channels).map(([key, value]) => {
                        if (value > 0) {
                          const channelIcons: Record<string, string> = { 
                            native: '📱', email: '✉️', sms: '💬', facebook: '📘', twitter: '🐦', kakao: '💛', whatsapp: '💚' 
                          };
                          const channelNames: Record<string, string> = {
                            native: t('stats.nativeShare', '기본 공유'),
                            email: t('stats.emailShare', '이메일'),
                            sms: t('stats.smsShare', 'SMS'),
                            facebook: 'Facebook',
                            twitter: 'Twitter',
                            kakao: 'KakaoTalk',
                            whatsapp: 'WhatsApp'
                          };
                          return (
                            <div key={key} className="flex justify-between items-center">
                              <span className="text-gray-600">{channelIcons[key] || '🔗'} {channelNames[key] || key}</span>
                              <span className="font-semibold text-gray-800">{value}{t('stats.units.times')}</span>
                            </div>
                          );
                        }
                        return null;
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : (
            <NoDataMessage message={t('stats.noShareData')} />
          )}
        </div>

        {/* QR 코드 통계 카드 */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center mb-4 pb-2 border-b border-gray-200">
            <div className="text-teal-600 mr-3">📱</div>
            <h3 className="text-lg font-bold text-gray-800">{t('stats.qrCodeStats')}</h3>
          </div>

          {(stats?.qrScans?.total >= 0) ? (
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('stats.totalScans')}</span>
              <span className="font-semibold text-gray-800">{stats.qrScans.total}{t('stats.units.times')}</span>
            </div>
          ) : (
            <NoDataMessage message={t('stats.noQrData')} />
          )}
        </div>

        {/* vCard 다운로드 통계 카드 */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center mb-4 pb-2 border-b border-gray-200">
            <div className="text-pink-600 mr-3">⬇️</div>
            <h3 className="text-lg font-bold text-gray-800">{t('stats.vcardStats') || 'vCard 다운로드'}</h3>
          </div>
          
          {(stats?.vcardDownloads?.total >= 0) ? (
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">{t('stats.totalDownloads') || '총 다운로드'}</span>
                <span className="font-semibold text-gray-800">{stats.vcardDownloads.total}{t('stats.units.times')}</span>
              </div>
              
              {stats.conversionRates?.downloadRate > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">{t('stats.downloadRate') || '다운로드율'}</span>
                  <span className="font-semibold text-gray-800">{stats.conversionRates.downloadRate.toFixed(1)}%</span>
                </div>
              )}
            </div>
          ) : (
            <NoDataMessage message={t('stats.noDownloadData') || 'vCard 다운로드 데이터가 없습니다'} />
          )}
        </div>

        {/* URL 복사 통계 카드 */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center mb-4 pb-2 border-b border-gray-200">
            <div className="text-purple-600 mr-3">📋</div>
            <h3 className="text-lg font-bold text-gray-800">{t('stats.urlCopyStats') || 'URL 복사'}</h3>
          </div>
          
          {(stats?.urlCopies?.total >= 0) ? (
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('stats.totalCopies') || '총 복사 횟수'}</span>
              <span className="font-semibold text-gray-800">{stats.urlCopies.total}{t('stats.units.times')}</span>
            </div>
          ) : (
            <NoDataMessage message={t('stats.noCopyData') || 'URL 복사 데이터가 없습니다'} />
          )}
        </div>

        {/* 전환율 통계 카드 */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center mb-4 pb-2 border-b border-gray-200">
            <div className="text-orange-600 mr-3">📈</div>
            <h3 className="text-lg font-bold text-gray-800">{t('stats.conversionStats')}</h3>
          </div>

          {(stats?.conversionRates) ? (
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">{t('stats.contactClickRate')}</span>
                <span className="font-semibold text-gray-800">{stats.conversionRates.contactClickRate.toFixed(1)}%</span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-gray-600">{t('stats.linkClickRate')}</span>
                <span className="font-semibold text-gray-800">{stats.conversionRates.linkClickRate.toFixed(1)}%</span>
              </div>

              {/* 클릭 상세 정보 */}
              {stats.clickDetails && (stats.clickDetails.totalContactClicks > 0 || stats.clickDetails.linkClicks > 0) && (
                <>
                  <div className="mt-4 pt-3 border-t border-gray-200">
                    <h4 className="font-semibold text-gray-800 mb-3">{t('stats.clickDetails') || '클릭 상세'}</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">{t('stats.totalContactClicks') || '총 연락처 클릭'}</span>
                        <span className="font-semibold text-gray-800">{stats.clickDetails.totalContactClicks}{t('stats.units.times')}</span>
                      </div>

                      {stats.clickDetails.phoneClicks > 0 && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">📞 {t('stats.phoneClicks') || '전화 클릭'}</span>
                          <span className="font-semibold text-gray-800">{stats.clickDetails.phoneClicks}{t('stats.units.times')}</span>
                        </div>
                      )}

                      {stats.clickDetails.emailClicks > 0 && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">✉️ {t('stats.emailClicks') || '이메일 클릭'}</span>
                          <span className="font-semibold text-gray-800">{stats.clickDetails.emailClicks}{t('stats.units.times')}</span>
                        </div>
                      )}

                      {stats.clickDetails.websiteClicks > 0 && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">🌐 {t('stats.websiteClicks') || '웹사이트 클릭'}</span>
                          <span className="font-semibold text-gray-800">{stats.clickDetails.websiteClicks}{t('stats.units.times')}</span>
                        </div>
                      )}

                      {stats.clickDetails.socialClicks > 0 && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">📱 {t('stats.socialClicks') || '소셜 클릭'}</span>
                          <span className="font-semibold text-gray-800">{stats.clickDetails.socialClicks}{t('stats.units.times')}</span>
                        </div>
                      )}

                      {stats.clickDetails.linkClicks > 0 && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">🔗 {t('stats.linkClicks') || '링크 클릭'}</span>
                          <span className="font-semibold text-gray-800">{stats.clickDetails.linkClicks}{t('stats.units.times')}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* 전환율 해석 도움말 */}
              {(stats.views.total > 0 && stats.clickDetails) && (
                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 text-center">
                    💡 {t('stats.conversionHelp', { count: stats.views.total, clicks: stats.clickDetails.totalContactClicks })}
                  </p>
                </div>
              )}
            </div>
          ) : (
            <NoDataMessage message={t('stats.noConversionData')} />
          )}
        </div>

        {/* 종합 전환율 요약 카드 */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center mb-4 pb-2 border-b border-gray-200">
            <div className="text-slate-600 mr-3">📊</div>
            <h3 className="text-lg font-bold text-gray-800">{t('stats.engagementSummary') || '참여율 요약'}</h3>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-1">{t('stats.viewToContact') || '연락처 클릭률'}</p>
              <p className="text-xl font-bold text-gray-800">{stats.conversionRates?.contactClickRate.toFixed(1)}%</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-1">{t('stats.viewToShare') || '공유율'}</p>
              <p className="text-xl font-bold text-gray-800">{stats.conversionRates?.shareRate.toFixed(1)}%</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-1">{t('stats.viewToDownload') || '다운로드율'}</p>
              <p className="text-xl font-bold text-gray-800">{stats.conversionRates?.downloadRate.toFixed(1)}%</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-1">{t('stats.viewToLink') || '링크 클릭률'}</p>
              <p className="text-xl font-bold text-gray-800">{stats.conversionRates?.linkClickRate.toFixed(1)}%</p>
            </div>
          </div>
          
          {/* 참여율 해석 도움말 */}
          {(stats.views?.total > 0) && (
            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 text-center">
                💡 {t('stats.engagementHelp', { totalViews: stats.views.total, interactions: (stats.shares?.total || 0) + (stats.vcardDownloads?.total || 0) + (stats.urlCopies?.total || 0) })}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StatsDetailPage;